import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { NO_DATA_TEXT } from "../../../../../constants";
import { ShaDowBox } from "../../../../../components/boxes";
import { TenancyData } from "../../../../../components/parts";
import {
  capitalize,
  getConditionalText,
  getData,
  getPercent,
  nFormatter,
} from "../../../../../services";
import OfficeImg from "../../../../../assets/images/icons/office.svg";
import RetailImg from "../../../../../assets/images/icons/retail.svg";
import LogisticsImg from "../../../../../assets/images/icons/logistics.svg";
import ResidentialImg from "../../../../../assets/images/icons/residential.svg";
import HospitalityImg from "../../../../../assets/images/icons/hospitality.svg";
import DataCentersImg from "../../../../../assets/images/icons/data-centers.svg";
import { isNotNull } from "../../../../../services/utils";
import { Tooltip, Typography, makeStyles } from "@material-ui/core";
import { COLORS } from "../../../../../theme/colors";

const imageSrc = {
  office: OfficeImg,
  residential: ResidentialImg,
  logistic: LogisticsImg,
  retail: RetailImg,
  hospitality: HospitalityImg,
  "data-centers": DataCentersImg,
};

const defaultResults = (unitSystem) => [
  {
    title: "Segment",
    value: NO_DATA_TEXT,
  },
  {
    title: "Units",
    value: 0,
  },
  {
    title: "Tenants",
    value: 0,
  },
  {
    title: "Total Area",
    value: 0,
    unit: unitSystem,
  },
  {
    title: "Roof Area",
    value: 0,
    unit: unitSystem,
  },
  {
    title: "Public Area",
    value: 0,
    unit: `${unitSystem} (0)`,
    button: "compare",
  },
  {
    title: "Electricity Meters",
    value: 0,
  },
  {
    title: "Meters Connected",
    value: 0,
    unit: "(0% of total)",
  },
];

const useStyles = makeStyles((theme) => ({
  moreSegments: {
    marginLeft: theme.spacing(1),
    cursor: "pointer",
  },
}));

export const TenancyDataInfo = ({ buildingId, complexId }) => {
  const classes = useStyles();

  const { user } = useSelector((state) => state.auth);
  const [results, setResults] = useState(defaultResults(user.unit_system));

  const url = buildingId
    ? `/praas/v2/buildings/${buildingId}/properties/`
    : `/praas/v2/complexes/${complexId}/properties/`;

  useEffect(() => {
    getData(url, (building) => {
      const segmentDisplay = () => {
        const segments = building?.segments?.length ? building?.segments : [building?.segment];
        if (segments?.length) {
          return (
            <div>
              {capitalize(segments[0])}
              {segments.length > 1 && (
                <Tooltip
                  title={segments.slice(1).map((segment, i) => (
                    <Typography key={i} style={{ color: COLORS.white }} variant="body2">
                      {capitalize(segment)}
                    </Typography>
                  ))}
                  placement="right"
                >
                  <Typography variant="span" className={classes.moreSegments}>
                    + {segments.length - 1}
                  </Typography>
                </Tooltip>
              )}
            </div>
          );
        }

        if (buildingId && building?.segment) {
          return getConditionalText(isNotNull(building.segment), building.segment);
        }

        return "-";
      };

      const segmentImage = () => {
        if (buildingId && building.segment) {
          return imageSrc[building.segment?.toLowerCase()];
        }

        if (complexId && building.segments?.length) {
          return imageSrc[building.segments[0]?.toLowerCase()];
        }

        return null;
      };

      setResults([
        {
          title: "Segment",
          value: segmentDisplay(),
          image_src: segmentImage(),
          image_style: {
            width: 36,
          },
        },
        {
          title: "Units",
          value: getConditionalText(
            isNotNull(building.unit_count),
            nFormatter(building.unit_count),
          ),
        },
        {
          title: "Tenants",
          value: getConditionalText(
            isNotNull(building.tenant_count),
            nFormatter(building.tenant_count),
          ),
        },
        {
          title: "Total Area",
          value: getConditionalText(
            isNotNull(building.total_area),
            nFormatter(building.total_area),
          ),
          unit: isNotNull(building.total_area) ? user?.unit_system : "",
        },
        {
          title: "Roof Area",
          value: getConditionalText(isNotNull(building.roof_area), nFormatter(building.roof_area)),
          unit: isNotNull(building.roof_area) ? user?.unit_system : "",
        },
        {
          title: "Public Area",
          value: getConditionalText(
            isNotNull(building.public_area),
            nFormatter(building.public_area),
          ),
          unit: isNotNull(building.public_area)
            ? `${user?.unit_system} (${getPercent(building.public_area, building.total_area, 1)}%)`
            : "",
          button: isNotNull(building.public_area) ? "compare" : "",
        },
        {
          title: "Electricity Meters",
          value: getConditionalText(
            isNotNull(building.electricity_meters),
            nFormatter(building.electricity_meters),
          ),
        },
        {
          title: "Meters Connected",
          value: getConditionalText(
            isNotNull(building.meters_connected),
            nFormatter(building.meters_connected),
          ),
          unit: isNotNull(building.meters_connected)
            ? `(${getPercent(building.meters_connected, building.electricity_meters)}% of total)`
            : "",
        },
      ]);
    });

    return () => setResults(defaultResults(user.unit_system));
  }, [buildingId, complexId]);

  return (
    <ShaDowBox>
      <TenancyData results={results} />
    </ShaDowBox>
  );
};
